import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Facebook, Mail, Phone, MapPin, Globe2 } from 'lucide-react';
import { SERVICES, INDUSTRIES, MARKETS } from '../mock';

// Inline WhatsApp brand SVG — lucide-react doesn't ship brand icons.
function WhatsAppIcon({ size = 18 }) {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={size} height={size} viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
      <path d='M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .17 5.32.17 11.87c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.66a11.88 11.88 0 0 0 5.7 1.45h.01c6.55 0 11.88-5.32 11.88-11.87 0-3.17-1.24-6.16-3.42-8.44Zm-8.47 18.24h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.77.99 1-3.67-.23-.38a9.87 9.87 0 0 1-1.5-5.2c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.13 1.03 7 2.9a9.85 9.85 0 0 1 2.9 7c0 5.45-4.44 9.83-9.9 9.83Zm5.43-7.37c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.78-1.68-2.08-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.22 1.35.19 1.86.11.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z' />
    </svg>
  );
}

const SOCIALS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/ransen/', Icon: Linkedin },
  { label: 'Facebook', href: 'https://www.facebook.com/people/Ransen-Marketing-Lab/61587451496930/', Icon: Facebook },
  { label: 'Instagram', href: 'https://www.instagram.com/ransen_marketing/', Icon: Instagram },
  { label: 'WhatsApp', href: 'https://wa.me/13435732394', Icon: WhatsAppIcon },
];

export default function Footer() {
  return (
    <footer className='relative z-40 mt-24 border-t-2 border-brand'>
      <div className='absolute inset-0 bg-[#1e1230]' style={{ zIndex: -1 }} />
      <div className='max-w-7xl mx-auto px-6 pt-20 pb-10 relative'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12'>
          <div className='lg:col-span-2'>
            <Link to='/' className='flex items-center space-x-3 mb-6'>
              <div className='w-14 h-14 rounded-xl overflow-hidden shadow-lg shadow-brand/40'>
                <img src='/logo.png' alt='Ransen' className='w-full h-full object-contain logo-motion' />
              </div>
              <div>
                <div className='text-2xl font-heading font-bold text-white'>RANSEN</div>
                <div className='text-sm text-brand font-medium'>AI-Driven Growth Agency</div>
              </div>
            </Link>
            <p className='text-white/85 max-w-md mb-6 leading-relaxed'>
              AI-Powered Growth for Ambitious Brands. Ransen delivers performance marketing, SEO, LLM optimization, analytics, and AI automation for companies ready to scale.
            </p>
            <p className='text-white/60 text-sm max-w-md mb-6 leading-relaxed'>
              Full-service digital marketing agency serving brands across North America, Europe, the Middle East, and APAC.
            </p>
            <div className='space-y-3'>
              <a href='mailto:marketing@ransen.ca' className='flex items-center text-white/85 hover:text-brand transition-colors font-medium' data-testid='footer-email'>
                <Mail size={18} className='mr-3 text-brand' />
                marketing@ransen.ca
              </a>
              <a href='tel:+13435732394' className='flex items-center text-white/85 hover:text-brand transition-colors font-medium' data-testid='footer-phone'>
                <Phone size={18} className='mr-3 text-brand' />
                +1 (343) 573-2394
              </a>
              <div className='flex items-center text-white/85'>
                <MapPin size={18} className='mr-3 text-brand' />
                Toronto · London · Dubai · Singapore
              </div>
            </div>
            <div className='flex items-center gap-3 mt-6'>
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className='w-10 h-10 rounded-full bg-white/10 hover:bg-brand/25 border border-white/20 hover:border-brand/60 flex items-center justify-center text-white/80 hover:text-brand transition-all hover:scale-110'
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <FooterCol title='Services'>
            {SERVICES.slice(0, 7).map((s) => (
              <FooterLink key={s.slug} to={`/services/${s.slug}`}>{s.title}</FooterLink>
            ))}
          </FooterCol>

          <FooterCol title='Industries'>
            {INDUSTRIES.map((i) => (
              <FooterLink key={i.slug} to={`/industries/${i.slug}`}>{i.title}</FooterLink>
            ))}
          </FooterCol>

          <FooterCol title='Locations'>
            {MARKETS.slice(0, 5).map((m) => (
              <FooterLink key={m.slug} to={`/markets/${m.slug}`}>{m.name}</FooterLink>
            ))}
            <FooterLink to='/locations'>All city hubs →</FooterLink>
            <div className='pt-3'>
              <div className='text-white font-heading font-bold text-lg mb-3'>Company</div>
              <div className='flex flex-col gap-2.5'>
                <FooterLink to='/about'>About</FooterLink>
                <FooterLink to='/case-studies'>Case Studies</FooterLink>
                <FooterLink to='/blog'>Blog</FooterLink>
                <FooterLink to='/resources'>Resources</FooterLink>
                <FooterLink to='/contact'>Contact</FooterLink>
              </div>
            </div>
          </FooterCol>
        </div>

        <div className='border-t border-white/15 pt-8'>
          <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
            <p className='text-white/70 text-sm'>
              © {new Date().getFullYear()} Ransen. Global AI-Powered Growth Agency. All rights reserved.
            </p>
            <div className='flex items-center gap-5 text-sm'>
              <a href='#' className='text-white/60 hover:text-brand transition-colors'>Privacy</a>
              <a href='#' className='text-white/60 hover:text-brand transition-colors'>Terms</a>
              <a href='https://ransen.ca' target='_blank' rel='noreferrer' className='text-white/60 hover:text-brand transition-colors flex items-center gap-1.5'>
                <Globe2 className='w-3.5 h-3.5' /> ransen.ca
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className='font-heading font-bold text-white mb-5 text-lg'>{title}</h4>
      <div className='flex flex-col gap-2.5'>{children}</div>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} className='text-white/80 hover:text-brand transition-colors text-[14px]'>{children}</Link>
  );
}
